/**
 * DubbingResultsSection Component
 *
 * Section displaying dubbing results with download button.
 */

import { memo } from 'react';
import { Download, FolderOpen } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { isDesktopApp } from '@/utils/apiMode';
import type { DubbingResponse } from '@/types';
import type { MediaType } from '@/components/common/MediaUploader';
import type { AudioDubbingResponse } from '@/services/dubbing';

interface DubbingResultsSectionProps {
  dubbingResult: DubbingResponse | null;
  audioDubbingResult: AudioDubbingResponse | null;
  /** What the user actually uploaded. Drives all media-specific wording. */
  mediaType: MediaType | null;
  targetLanguagesCount: number;
  downloadUrl: string | null;
  /** Local path of the artifact; present only for jobs that ran on this machine. */
  localOutputPath: string | null;
  onDownload: () => void;
  onOpenFolder: () => void;
  onReset: () => void;
}

export const DubbingResultsSection = memo(function DubbingResultsSection({
  dubbingResult,
  audioDubbingResult,
  mediaType,
  targetLanguagesCount,
  downloadUrl,
  localOutputPath,
  onDownload,
  onOpenFolder,
  onReset,
}: DubbingResultsSectionProps) {
  const { t } = useTranslation();

  // What the user uploaded decides the wording. This used to be
  // `audioDubbingResult?.success`, which depended on an upstream discriminator
  // that could never fire — so dubbing an MP3 announced "Your video has been
  // successfully dubbed" and offered "Download Dubbed Video".
  const isAudioResult = mediaType === 'audio';

  // Reveal-in-Finder replaces the download only when there is genuinely a
  // local file to reveal: the desktop app running a cloud job has no local
  // path and must still download over HTTP.
  const showOpenFolder = isDesktopApp() && Boolean(localOutputPath);

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-secondary-800 rounded-lg border border-gray-200 dark:border-secondary-700 p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
          <Download className="h-5 w-5" />
          <span>{t('dubbing.sections.results', { defaultValue: 'Dubbing Results' })}</span>
        </h3>

        <div className="space-y-4">
          {/* Success message */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-sm text-green-800 dark:text-green-200">
              {isAudioResult
                ? t('dubbing.results.audioSuccessMessage', {
                    count: audioDubbingResult?.languages_processed?.length || targetLanguagesCount,
                  })
                : t('dubbing.results.successMessage', {
                    count: targetLanguagesCount,
                  })}
            </p>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {/* Processing time - only show if > 0 */}
            {((dubbingResult?.processing_time_seconds ?? 0) > 0 ||
              (audioDubbingResult?.processing_time_seconds ?? 0) > 0) && (
              <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('dubbing.results.processingTime', { defaultValue: 'Processing Time' })}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {Math.round(
                    dubbingResult?.processing_time_seconds ||
                      audioDubbingResult?.processing_time_seconds ||
                      0
                  )}
                  s
                </p>
              </div>
            )}

            {/* Segments (video only) */}
            {dubbingResult?.segments_processed && (
              <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('dubbing.results.segmentsProcessed', { defaultValue: 'Segments' })}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {dubbingResult.segments_processed}
                </p>
              </div>
            )}

            {/* Detected language (audio only) */}
            {audioDubbingResult?.detected_language && (
              <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('dubbing.results.detectedLanguage', { defaultValue: 'Detected Language' })}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {audioDubbingResult.detected_language.toUpperCase()}
                </p>
              </div>
            )}

            {/* Audio tracks */}
            {(dubbingResult?.audio_tracks || audioDubbingResult?.audio_tracks) && (
              <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                  {t('dubbing.results.audioTracks', { defaultValue: 'Audio Tracks' })}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {
                    Object.keys(
                      dubbingResult?.audio_tracks || audioDubbingResult?.audio_tracks || {}
                    ).length
                  }
                </p>
              </div>
            )}

            {/* Languages processed (audio only) - only show if array has items */}
            {audioDubbingResult?.languages_processed &&
              audioDubbingResult.languages_processed.length > 0 && (
                <div className="bg-gray-50 dark:bg-secondary-700 rounded-lg p-3">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                    {t('dubbing.results.languagesProcessed', { defaultValue: 'Languages' })}
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">
                    {audioDubbingResult.languages_processed.join(', ').toUpperCase()}
                  </p>
                </div>
              )}
          </div>

          {/* Primary action: reveal the local file, or download it */}
          {(downloadUrl || showOpenFolder) && (
            <div className="flex justify-center pt-4">
              {showOpenFolder ? (
                <button
                  onClick={onOpenFolder}
                  data-testid="dubbing-open-folder-button"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <FolderOpen className="w-5 h-5 mr-2" />
                  {t('result.buttons.showInFinder')}
                </button>
              ) : (
                <button
                  onClick={onDownload}
                  data-testid="dubbing-download-button"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover:from-green-700 hover:to-blue-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  <Download className="w-5 h-5 mr-2" />
                  {isAudioResult
                    ? t('dubbing.buttons.downloadAudio')
                    : t('dubbing.buttons.downloadVideo')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reset button */}
      <div className="flex justify-center">
        <button
          onClick={onReset}
          className="px-6 py-2 bg-gray-100 dark:bg-secondary-700 text-gray-700 dark:text-gray-200 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-secondary-600 transition-colors"
        >
          {t('dubbing.buttons.dubAnother', { defaultValue: 'Dub Another File' })}
        </button>
      </div>
    </div>
  );
});
